import {
  mdiFileCodeOutline,
  mdiFileDocumentOutline,
  mdiFileOutline,
  mdiFilePdfBox,
  mdiFileTableOutline,
  mdiFolderZipOutline,
  mdiFileImageOutline,
  mdiFileCogOutline,
} from "@mdi/js";

export const fileEncodings = {
  UTF8: "UTF-8",
  BINARY: "binary",
  BASE64: "base64",
};

export function parseEncoding(readerEvent, encoding) {
  switch (encoding) {
    case fileEncodings.BASE64:
      return "base64://" + readerEvent.target.result.split(";base64,")[1];
    default:
      return readerEvent.target.result;
  }
}

export function openFileDialog({
  accept,
  encoding = fileEncodings.UTF8,
  onLoad,
}) {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = (e) => {
      onLoad && onLoad();
      const file = e.target.files[0];
      const reader = new FileReader();
      if (encoding === fileEncodings.UTF8) reader.readAsText(file, encoding);
      else if (encoding === fileEncodings.BASE64) reader.readAsDataURL(file);
      else if (encoding === fileEncodings.BINARY)
        reader.readAsArrayBuffer(file);
      reader.onload = (readerEvent) =>
        resolve({
          data: parseEncoding(readerEvent, encoding),
          filename: file.name,
          type: file.type,
        });
      reader.onerror = reject;
    };
    input.click();
  });
}

export function loadFile({ file, encoding = fileEncodings.UTF8 }) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    if (encoding === fileEncodings.UTF8) reader.readAsText(file, encoding);
    else if (encoding === fileEncodings.BASE64) reader.readAsDataURL(file);
    else if (encoding === fileEncodings.BINARY) reader.readAsArrayBuffer(file);
    reader.onload = (readerEvent) =>
      resolve({
        data: parseEncoding(readerEvent, encoding),
        filename: file.name,
        type: file.type,
      });
    reader.onerror = reject;
  });
}

export function unpackMime(mime) {
  switch (mime) {
    case "application/pdf":
      return {
        category: "Document",
        iconPath: mdiFilePdfBox,
        fileTypeKey: "Type::PDF",
        extension: "pdf",
      };
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return {
        category: "Document",
        iconPath: mdiFileDocumentOutline,
        fileTypeKey: "Type::MSWord",
        extension: "docx",
      };
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return {
        category: "Document",
        iconPath: mdiFileTableOutline,
        fileTypeKey: "Type::MSExcel",
        extension: "xlsx",
      };
    case "application/xml":
    case "text/xml":
      return {
        category: "Code",
        iconPath: mdiFileCodeOutline,
        fileTypeKey: "Type::XML",
        extension: "xml",
      };
    case "application/zip":
      return {
        category: "Unknown",
        iconPath: mdiFolderZipOutline,
        fileTypeKey: "Type::ZIP",
        extension: "zip",
      };
    case "text/csv":
      return {
        category: "Code",
        iconPath: mdiFileTableOutline,
        fileTypeKey: "Type::CSV",
        extension: "csv",
      };
    case "application/x-msdownload":
      return {
        category: "Unknown",
        iconPath: mdiFileCogOutline,
        fileTypeKey: "Type::Executable",
        extension: "msi",
      };
    default:
      if (mime.startsWith("image/")) {
        return {
          category: "Image",
          iconPath: mdiFileCogOutline,
          fileTypeKey: "Type::Image",
          extension: mime.replace("image/", ""),
        };
      }
      if (mime.endsWith("+xml")) {
        return {
          category: "Code",
          iconPath: mdiFileCodeOutline,
          fileTypeKey: "Type::XML",
          extension: "xml",
        };
      }
      return {
        category: "Unknown",
        iconPath: mdiFileOutline,
        fileTypeKey: "Type::Generic",
        extension: null,
      };
  }
}

export function splitFilename(filename, suffixCount = 1, separator = ".") {
  const arr = filename.split(".");
  let endingArr = [];
  let realSuffixCount = suffixCount === -1 ? arr.length - 1 : suffixCount;
  for (let i = 0; i < realSuffixCount; ++i) {
    if (arr.length > 1) endingArr = [...endingArr, arr.pop()];
  }

  return {
    ending: endingArr.reverse().join("."),
    name: arr.join("."),
    filename: filename,
  };
}

export function checkMimeCompatibility(mimeA, mimeB) {
  const group = mimeA.split("/")[0];
  switch (group) {
    case "image":
      return mimeB.startsWith("image/");
    default:
      return mimeA === mimeB;
  }
}
